import { appSettings } from "../AppSettings";
import { event } from "../Event";
import { userSettingsStore } from "../Services/UserSettingsStore";
import { ICameraScanner, ICameraScannerCapabilities } from "./Abstraction/ICameraScanner";
import { IScannerCode, IScanOptions } from "./Abstraction/IScanner";
import { delayAsync } from "./Utils";

export interface IBaseCameraScannerOptions {
    useCanvas: boolean;
}


export abstract class BaseCameraScanner implements ICameraScanner {

    protected _activeCamera: number;
    protected _isScanning: boolean;
    protected _cameras: MediaDeviceInfo[];
    protected _isFlashOn: boolean;
    protected _isOpen: boolean;
    protected _focus: number;
    protected _useCanvas: boolean;
    protected _video: HTMLVideoElement;

    constructor(options?: IBaseCameraScannerOptions) {

        this._activeCamera = 0;
        this._isScanning = false;
        this._cameras = [];
        this._isFlashOn = false;
        this._isOpen = false;
        this._focus = -1;
        this._useCanvas = options?.useCanvas;
    }


    abstract setCameraAsync(index: number): Promise<void>;

    abstract scanSingleAsync(setting?: IScanOptions): Promise<IScannerCode|null>;

    abstract stopScanAsync(): Promise<void>;

    protected abstract getVideoWork(): HTMLVideoElement;

    /**************************************/

    async openAsync() {

        if (this._isOpen)
            return;

        await this.updateCamerasAsync();

        await this.openWorkAsync();

        this._isOpen = true;
    }

    /**************************************/

    async closeAsync() {
        if (this._isScanning)
            await this.stopScanAsync();
    }

    /**************************************/

    getVideo(reset?: boolean): HTMLVideoElement {
        if (this._useCanvas) {

            if (!this._video || reset) {

                if (this._video)
                    this._video.pause();

                this._video = document.createElement("video");
            }
     
            return this._video;
        }
        return this.getVideoWork();
    }

    /**************************************/

    protected async openWorkAsync() {

    }

    /**************************************/

    async startMultiScanAsync(onResult: (result: IScannerCode) => Promise<boolean>, options?: IScanOptions) {

        const settings = userSettingsStore.current;

        while (true) {

            const result = await this.scanSingleAsync(options);
            if (!result)
                return;
            const mustContinue = await onResult(result);
            if (!mustContinue)
                return;

            const scanDelay = settings.scanDelay !== undefined ? settings.scanDelay : appSettings.scanDelay;

            await delayAsync(scanDelay);
        }
    }

    /**************************************/

    protected async updateCamerasAsync() {

        this._cameras = [];

        const devices = await navigator.mediaDevices.enumerateDevices();
        for (const device of devices) {
            console.log(JSON.stringify(device));
            if (device.kind == "videoinput") {
                this._cameras.push(device);
            }
        }
    }


    /**************************************/

    protected async updateCapsAsync() {

        try {

            await this.updateFlashAsync(this._isFlashOn);

            await this.updateFocusAsync(this._focus);
        }
        catch (ex) {
            console.warn(ex);
        }
    }

    /**************************************/

    protected async waitForScannerAsync() {

        console.debug("waitForScannerAsync");

        while (this._isScanning) {

            if (this.isCameraReady()) {
                this.onScannerReady.raise(this, undefined);
                return true;
            }

            await delayAsync(500);
        }

        return false;
    }

    /**************************************/

    async switchCameraAsync() {

        if (this._cameras.length == 1)
            return;

        this._activeCamera = (this._activeCamera + 1) % this._cameras.length;

        await this.setCameraAsync(this._activeCamera);
    }

    /**************************************/

    async updateFocusAsync(focus: number) {

        const videoObj = this.getVideo();

        if (!videoObj?.srcObject)       
            return true;

        try {
            const track = (videoObj.srcObject as MediaStream).getVideoTracks()[0];
            const capabilities = track.getCapabilities();

            if ("focusMode" in capabilities && "focusDistance" in capabilities) {

                if (focus == -1) {
                    track.applyConstraints({
                        advanced: [{
                            focusMode: "continuous"
                        }]
                    });
                }
                else {
                    const focusDistance = capabilities.focusDistance.min + (capabilities.focusDistance.max - capabilities.focusDistance.min) * (focus / 100);

                    track.applyConstraints({
                        advanced: [{
                            focusMode: "manual",
                            focusDistance
                        }]
                    });
                }

                this._focus = focus;

                return true;
            }

            return false;
        }
        catch (ex) {
            console.log(ex);
            return false;
        }
    }

    /**************************************/

    getCapabilities() : ICameraScannerCapabilities {

        const videoObj = this.getVideo();

        if (!videoObj?.srcObject)
            return null;

        const track = (videoObj.srcObject as MediaStream).getVideoTracks()[0];
        const capabilities = track.getCapabilities();
        return {
            focus: "focusMode" in capabilities && "focusDistance" in capabilities,
            flash: "torch" in capabilities
        }
    }

    /**************************************/

    async updateFlashAsync(isOn: boolean) {

        const videoObj = this.getVideo();

        if (videoObj?.srcObject) {

            try {
                const track = (videoObj.srcObject as MediaStream).getVideoTracks()[0];
                const capabilities = track.getCapabilities();

                if ("torch" in capabilities) {
                    track.applyConstraints({ advanced: [{ torch: isOn }] });
                    this._isFlashOn = isOn;
                    return true;
                }
                return false;
            }
            catch (ex) {
                console.warn(ex);
                return false;
            }
        }

        return true;
    }

    /**************************************/

    protected isCameraReady() {

        const videoObj = this.getVideo();

        return videoObj?.srcObject && (videoObj.srcObject as MediaStream).getTracks()[0].readyState == "live";
    }

    /**************************************/

    get isScanning() {
        return this._isScanning;
    }

    get isOpen() {
        return this._isOpen;
    }

    get activeCamera() {
        return this._activeCamera;
    }

    get cameras() {
        return this._cameras;
    }

    get isFlashOn() {
        return this._isFlashOn;
    }

    get focus() {
        return this._focus;
    }

    readonly onScannerReady = event<void>();
}